import React, { useState, useRef, useEffect, ReactElement, CSSProperties } from 'react';
import { useNavigate } from "react-router-dom";
import { filterJobRole, getHiringManagerName } from './utils/jobRoleFilters';
import { JobRole } from '../../types';

interface RoleTableListProps {
  jobRoles: JobRole[];
  isAdmin: boolean;
  filterQuery: string;
  onDuplicate: (jobRole: JobRole) => void;
  onMarkHired: (jobRole: JobRole) => void;
}

type ActionType = 'Edit' | 'Duplicate' | 'Mark Hired and Close' | 'Archived';

const ACTION_LIST: ActionType[] = ['Edit', 'Duplicate', 'Mark Hired and Close', 'Archived'];

const RoleTableList = ({
  jobRoles,
  isAdmin,
  filterQuery,
  onDuplicate,
  onMarkHired,
}: RoleTableListProps): ReactElement => {
  const navigate = useNavigate();
  
  // 2. Typed State and Refs
  const [showActionList, setShowActionList] = useState<string | null>(null);
  const [position, setPosition] = useState<CSSProperties>({ top: '100%', bottom: 'auto' });
  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (showActionList && dropdownRef.current) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (dropdownRect.bottom > viewportHeight) {
        setPosition({ top: 'auto', bottom: '100%' });
      } else {
        setPosition({ top: '100%', bottom: 'auto' });
      }
    }
  }, [showActionList]);

  useEffect(() => {
    if (!showActionList) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('.more-horiz')) return;

      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setShowActionList(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActionList]);

  const handleButtonClick = (jobRoleId: string): void => {
    setShowActionList(showActionList === jobRoleId ? null : jobRoleId);
  };

  const handleAction = (action: ActionType, jobRole: JobRole): void => {
    setShowActionList(null);
    
    if (action === 'Edit') {
      navigate(`/admin/jobrole?compID=${jobRole.company.id}`, {
        state: { currentJobRole: jobRole, mode: 'edit' }
      });
    } else if (action === 'Duplicate') {
      onDuplicate(jobRole);
    } else if (action === 'Mark Hired and Close') {
      onMarkHired(jobRole);
    } else if (action === 'Archived') {
      // TODO handle archive
    }
  };

  if (jobRoles.length === 0) {
    return <div>Click on 'New Role' button to begin</div>;
  }

  return (
    <div style={styles.listTable}>
      <div style={styles.listHeader}>
        <div style={styles.titleHeaderStyle}>Title</div>
        {isAdmin && (
          <div style={styles.columnStyle}>
            <div>Company</div>
            <div style={{ fontSize: 9 }}>(Super Admins Only)</div>
          </div>
        )}
        <div style={styles.columnStyle}>Hiring Manager</div>
        <div style={styles.columnStyle}>Created</div>
        <div style={styles.columnStyle}>Status</div>
        <div style={styles.actionColumnStyle}>Actions</div>
      </div>

      <div style={styles.listContent}>
        {jobRoles.filter(jobRole => filterJobRole(jobRole, filterQuery)).map((jobRole) => (
          <div key={jobRole.id} style={styles.listRowContent}>
            <button
              style={styles.titleColumnStyle}
              onClick={() =>
                navigate(`/admin/jobrole?compID=${jobRole.company.id}`, {
                  state: { currentJobRole: jobRole, mode: 'edit' }
                })
              }
            >
              {jobRole.jobRoleData.title}
            </button>
            {isAdmin && (
              <div style={styles.columnStyle}>{jobRole.company.company}</div>
            )}
            <div style={styles.columnStyle}>
              {jobRole.hiringManager
                ? getHiringManagerName(jobRole)
                : '-'}
            </div>
            <div style={styles.columnStyle}>{jobRole.formattedDate}</div>
            <div style={styles.columnStyle}>{jobRole.status}</div>
            <div style={styles.actionColumnStyle}>
              <button
                type="button"
                className="more-horiz"
                onClick={() => handleButtonClick(jobRole.id)}
                aria-label="More actions"
              />
              {showActionList === jobRole.id && (
                <ul ref={dropdownRef} style={{ ...styles.dropDownAction, ...position }}>
                  {ACTION_LIST.map((action) => (
                    <li key={action}>
                      <button
                        style={styles.listItemStyle}
                        onMouseEnter={(e) => {
                          const hoverBg = styles.listItemHoverStyle?.backgroundColor;
                          if (hoverBg) e.currentTarget.style.backgroundColor = hoverBg as string;
                        }}
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = 'transparent')
                        }
                        onClick={() => handleAction(action, jobRole)}
                      >
                        {action}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// 3. Typed Styles object
const styles: Record<string, CSSProperties> = {
  listTable: { display: "flex", flexDirection: "column" },
  listHeader: {
    display: "flex",
    color: "var(--Primary-Text, #374151)",
    justifyContent: "space-between",
    fontFamily: "Inter",
    fontSize: "16px",
    fontWeight: 600
  },
  listContent: {
    borderTop: "1px solid var(--Stroke, #DFE4EA)",
    height: "100%",
    overflow: "scroll",
    maxHeight: "calc(100vh - 248px)",
    scrollBehavior: "smooth"
  },
  listRowContent: {
    display: "flex",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 400,
    justifyContent: "space-between",
  },
  titleHeaderStyle: { flex: 1, padding: '8px', textAlign: 'left' },
  titleColumnStyle: {
    flex: 1,
    padding: '8px',
    textAlign: 'left',
    textDecoration: 'underline',
    color: 'var(--Primary, navy)',
    background: 'none',
    border: 'none',
    cursor: 'pointer'
  },
  columnStyle: { flex: 0.75, padding: '8px', textAlign: 'left' },
  actionColumnStyle: {
    flex: 0.2,
    padding: '8px',
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'relative',
    marginRight: 8
  },
  dropDownAction: {
    position: 'absolute',
    right: 5,
    borderRadius: 16,
    background: "var(--White-White, #FFF)",
    boxShadow: "0px 1px 3px 0px rgba(166, 175, 195, 0.40)",
    zIndex: 1,
    listStyleType: 'none',
    padding: "16px 0px",
    margin: '0',
    width: 204,
  },
  listItemStyle: {
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
  },
  listItemHoverStyle: {
    backgroundColor: 'var(--Cyan-Cyan-light-3, #D0F0FD)',
  },
};

export default RoleTableList;